// @ts-strict-ignore
import Form, { useForm } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import FormItem from 'antd/lib/form/FormItem';
import Space from 'antd/lib/space';
import { ThunderboltFilled } from '@ant-design/icons';
import Radio from 'antd/lib/radio';
import { convertAndroidStagedRolloutPercentage, displayStoreNameForAppStoreType } from '../../helpers/displayHelpers';
import { AppStoreType, HuaweiAppGallerySettings } from '../../types';
import { AndroidStagedRolloutStyle } from '../../typescriptHelpers';
import { stagedRolloutPercentageInputValidationRules } from '@/helpers/formHelpers';
import { AndroidStagedRolloutPercentageInput, ReleaseOnlySettingsAlert } from '../sharedComponents/shared';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

interface EditHuaweiReleaseSettingsProps {
    isConfiguringAppDefaults: boolean;
    isWorkflowStep?: boolean;
    displayableVersion?: string;
    open: boolean;
    isLoading: boolean;
    customStagedRolloutEnabled: boolean;
    customStagedRolloutDaysToPercent?: number[];
    huaweiAppGallerySettings?: HuaweiAppGallerySettings;
    onSubmit: (stagedRelease: boolean, initialStagedRolloutPercentage?: number) => void;
    onCancel: () => void;
}

const EditHuaweiReleaseSettings: React.FC<EditHuaweiReleaseSettingsProps> = (props: EditHuaweiReleaseSettingsProps) => {
    const [form] = useForm();
    const [stagedReleaseEnabled, setStagedReleaseEnabled] = React.useState(false);

    const title =
        props.isConfiguringAppDefaults ?
            `Edit ${displayStoreNameForAppStoreType(AppStoreType.Huawei)} release defaults`
        :   `Edit release settings`;

    const requiresInitialStagedRollout =
        !props.customStagedRolloutEnabled ||
        (props.customStagedRolloutEnabled && !props.customStagedRolloutDaysToPercent);

    const onSubmit = (values) => {
        let stagedRelease = false;

        switch (values.androidStagedReleaseStyle as AndroidStagedRolloutStyle) {
            case AndroidStagedRolloutStyle.Staged:
                stagedRelease = true;
                break;
            case AndroidStagedRolloutStyle.NotStaged:
                stagedRelease = false;
                break;
        }

        const initialStagedRolloutPercentage =
            values.initialStagedRolloutPercentage ? values.initialStagedRolloutPercentage : null;

        props.onSubmit(stagedRelease, initialStagedRolloutPercentage);
    };

    React.useEffect(() => {
        if (props.open) {
            if (props.huaweiAppGallerySettings) {
                const androidStagedReleaseStyle =
                    props.huaweiAppGallerySettings.stagedRelease ?
                        AndroidStagedRolloutStyle.Staged
                    :   AndroidStagedRolloutStyle.NotStaged;
                let stagedRolloutPercentage: number | null;

                if (props.huaweiAppGallerySettings.initialStagedRolloutPercentage) {
                    stagedRolloutPercentage = convertAndroidStagedRolloutPercentage(
                        props.huaweiAppGallerySettings.initialStagedRolloutPercentage,
                    );
                } else if (props.customStagedRolloutEnabled) {
                    stagedRolloutPercentage =
                        props.customStagedRolloutDaysToPercent ? props.customStagedRolloutDaysToPercent[0] : null;
                }

                form.setFieldsValue({
                    androidStagedReleaseStyle: androidStagedReleaseStyle,
                    initialStagedRolloutPercentage: stagedRolloutPercentage,
                });

                setStagedReleaseEnabled(androidStagedReleaseStyle === AndroidStagedRolloutStyle.Staged);
            } else {
                form.setFieldsValue({
                    androidStagedReleaseStyle: null,
                    initialStagedRolloutPercentage: null,
                });

                setStagedReleaseEnabled(false);
            }
        }
    }, [props.open]);

    return (
        <Modal
            title={title}
            onOk={() => form.submit()}
            okButtonProps={{ loading: props.isLoading }}
            onCancel={props.onCancel}
            destroyOnClose
            open={props.open}
            okText={'Update'}
            cancelText="Cancel"
            afterClose={() => form.resetFields()}
            bodyStyle={{ backgroundColor: antGrey.grey3 }}
        >
            {props.isConfiguringAppDefaults ?
                <Text
                    style={{
                        paddingBottom: 12,
                        display: 'block',
                    }}
                >
                    Choose a default phased release behavior for any new release submissions.
                </Text>
            :   <ReleaseOnlySettingsAlert
                    displayableVersion={props.displayableVersion}
                    isWorkflowStep={!!props.isWorkflowStep}
                    onCloseModal={props.onCancel}
                />
            }
            <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                initialValues={{
                    androidStagedReleaseStyle: AndroidStagedRolloutStyle.NotStaged,
                    initialStagedRolloutPercentage: 25,
                }}
            >
                <FormItem label="Phased release" name="androidStagedReleaseStyle" rules={[{ required: true }]}>
                    <Radio.Group
                        onChange={(e) => {
                            setStagedReleaseEnabled(e.target.value === AndroidStagedRolloutStyle.Staged);
                        }}
                    >
                        <Space direction="vertical">
                            <Radio value={AndroidStagedRolloutStyle.NotStaged}>
                                Don&apos;t phase, release updates to all users immediately
                            </Radio>
                            <Radio value={AndroidStagedRolloutStyle.Staged}>
                                Phased release to a percentage of users
                            </Radio>
                        </Space>
                    </Radio.Group>
                </FormItem>
                {stagedReleaseEnabled && (
                    <FormItem
                        name="initialStagedRolloutPercentage"
                        label="Initial phased release percent"
                        rules={[
                            { required: requiresInitialStagedRollout },
                            ...stagedRolloutPercentageInputValidationRules,
                        ]}
                        help={
                            props.customStagedRolloutEnabled ?
                                <Text style={{ color: antGrey.grey7 }}>
                                    <ThunderboltFilled style={{ color: runwayColors.primary }} /> Automated custom
                                    phased release is enabled. The phased release percentage indicated for Day 1 of your
                                    custom staged rollout{' '}
                                    {(
                                        props.customStagedRolloutDaysToPercent &&
                                        props.customStagedRolloutDaysToPercent.length > 0
                                    ) ?
                                        `(${props.customStagedRolloutDaysToPercent[0]}%)`
                                    :   null}{' '}
                                    will be used.{' '}
                                    <TooltipInfoIcon
                                        hasCustomContent
                                        tooltipContent={
                                            <Space direction="vertical">
                                                <Space direction="vertical" size={0}>
                                                    {props.customStagedRolloutDaysToPercent?.map((percent, i) => {
                                                        return (
                                                            <Text style={{ color: 'white' }} key={i}>
                                                                Day {i + 1}: {percent}%
                                                            </Text>
                                                        );
                                                    })}
                                                </Space>
                                                <Text style={{ color: 'white' }}>
                                                    You can configure your custom staged rollout from automation
                                                    settings.
                                                </Text>
                                            </Space>
                                        }
                                    />
                                </Text>
                            :   null
                        }
                    >
                        <AndroidStagedRolloutPercentageInput disabled={!requiresInitialStagedRollout} />
                    </FormItem>
                )}
            </Form>
            {props.isConfiguringAppDefaults && (
                <Text type="secondary">These settings will apply to all releases for this app.</Text>
            )}
        </Modal>
    );
};

export default EditHuaweiReleaseSettings;
